import { useEffect } from "react";
import { TutorMode } from "@/constants/mode";
import { ASSESSMENT_KEYS } from "@/features/tutor/constants/assessmentTypes.js";
import { useAssessmentStore } from "@/features/tutor/store/useAssessmentStore.js";
import { useLatestRef } from "./useLatestRef";

export function useAssessmentRuntime({
  activeAssessmentModal,
  activeAssessmentType,
  currentSlideIndex,
  getSlideByIndex,
  handleInterrupt,
  mode,
  onLessonEnd,
  setActiveAssessmentModal,
  setCurrentSlideIndex,
  shouldInitializeSession,
  slides,
}) {
  const preAssessmentIsActive = useAssessmentStore(
    (s) => s.assessments.pre_assessment?.isActive ?? false,
  );
  // Ref so a searchParams change can't re-run the effect and end the lesson twice.
  const onLessonEndRef = useLatestRef(onLessonEnd);
  useEffect(() => {
    if (activeAssessmentModal || activeAssessmentType) {
      handleInterrupt();
    }
  }, [activeAssessmentModal, activeAssessmentType, handleInterrupt]);

  useEffect(() => {
    if (!slides || slides.length === 0 || !shouldInitializeSession) return;

    const currentSlide = getSlideByIndex(currentSlideIndex);
    const slideType = currentSlide?.metadata?.slide_type;
    if (!slideType || !ASSESSMENT_KEYS.includes(slideType)) return;

    if (mode === TutorMode.PREVIEW || slideType === "pre_assessment") {
      if (currentSlideIndex + 1 < slides.length) {
        setCurrentSlideIndex(currentSlideIndex + 1);
      } else if (slideType === "pre_assessment" && !preAssessmentIsActive) {
        // Not for preview's other assessments: handleLessonEnd can start a real exit-ticket attempt.
        // Not while a retake's survey is open; the effect re-runs when it closes.
        void onLessonEndRef.current?.();
      }
      return;
    }

    const store = useAssessmentStore.getState();
    const slice = store.assessments[slideType];
    if (!slice.has || !slice.id || slice.isActive) return;

    // Guard: suppress exit_ticket modal while pre_assessment is active. Subscribed
    // reactively so the effect re-runs when pre_assessment closes, allowing the
    // suppressed modal to open.
    if (preAssessmentIsActive) return;

    setActiveAssessmentModal(slideType);
  }, [
    currentSlideIndex,
    getSlideByIndex,
    mode,
    onLessonEndRef,
    preAssessmentIsActive,
    setActiveAssessmentModal,
    setCurrentSlideIndex,
    shouldInitializeSession,
    slides,
  ]);
}
